import type {
  GuestCheckoutAddress,
  GuestCheckoutCache,
  GuestCheckoutCacheOptions,
  GuestCheckoutCachePatch,
  GuestCheckoutStorage,
} from './types';

export const GUEST_CHECKOUT_CACHE_BASE_KEY = 'zl-guest-checkout-cache' as const;

const VISITOR_ID_STORAGE_KEY = 'zl-visitor_id';
/** 内部持久化 tombstone：阻止 billing 回退，且绝不通过 GuestCheckoutCache 对外暴露。 */
const BILLING_CLEARED_FIELD = '__guest_checkout_billing_cleared__';
const memoryCaches = new Map<string, string>();
const memoryFallbackKeys = new Set<string>();
const clearedCacheKeys = new Set<string>();

interface GuestCheckoutCacheReadResult {
  cache: GuestCheckoutCache | null;
  billingCleared: boolean;
}

const addressFields: Array<keyof GuestCheckoutAddress> = [
  'id',
  'is_pobox',
  'country_id',
  'country',
  'province_id',
  'province',
  'city',
  'street',
  'apartment',
  'postcode',
  'cpf',
  'tel_code',
  'tel_country',
  'telephone',
  'firstname',
  'lastname',
  'address',
  'default',
];

function getBrowserLocalStorage(): GuestCheckoutStorage | null {
  try {
    if (typeof globalThis.localStorage === 'undefined') return null;
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

function resolveStorage(storage?: GuestCheckoutStorage | null): GuestCheckoutStorage | null {
  return storage === undefined ? getBrowserLocalStorage() : storage;
}

function normalizeText(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim();
  return normalized || undefined;
}

function normalizeId(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return normalizeText(value);
}

function normalizeAddress(
  value: unknown,
  { createId = false }: { createId?: boolean } = {},
): GuestCheckoutAddress | undefined {
  if (!value || typeof value !== 'object') return undefined;

  const source = value as Record<string, unknown>;
  const address: GuestCheckoutAddress = {};

  for (const field of addressFields) {
    const fieldValue = source[field];
    if (fieldValue === undefined || fieldValue === null) continue;

    if (field === 'id') {
      const id = normalizeId(fieldValue);
      if (id) address.id = id;
      continue;
    }

    if (typeof fieldValue === 'string') {
      const normalized = normalizeText(fieldValue);
      if (normalized) address[field] = normalized as never;
      continue;
    }

    if (typeof fieldValue === 'number' && Number.isFinite(fieldValue)) {
      address[field] = fieldValue as never;
    }
  }

  if (createId && !address.id) address.id = createGuestShippingAddressId();
  return Object.keys(address).length > 0 ? address : undefined;
}

function normalizeCache(value: unknown): GuestCheckoutCache | null {
  if (!value || typeof value !== 'object') return null;

  const source = value as Record<string, unknown>;
  const cache: GuestCheckoutCache = {};
  const contactEmail = normalizeText(source.contactEmail);
  const shipping = normalizeAddress(source.shipping);
  const shippingAddressId = normalizeId(source.shippingAddressId) ?? shipping?.id;
  const billing = normalizeAddress(source.billing);

  if (contactEmail) cache.contactEmail = contactEmail;
  if (shipping) cache.shipping = shipping;
  if (shippingAddressId) cache.shippingAddressId = shippingAddressId;
  if (billing) cache.billing = billing;

  return Object.keys(cache).length > 0 ? cache : null;
}

function resolveVisitorId(
  storage: GuestCheckoutStorage | null,
  visitorId?: string,
): string | undefined {
  const explicitVisitorId = normalizeText(visitorId);
  if (explicitVisitorId) return explicitVisitorId;
  if (!storage) return undefined;

  try {
    return normalizeText(storage.getItem(VISITOR_ID_STORAGE_KEY));
  } catch {
    return undefined;
  }
}

function getCacheKey(storage: GuestCheckoutStorage | null, visitorId?: string): string {
  const resolvedVisitorId = resolveVisitorId(storage, visitorId);
  return resolvedVisitorId
    ? `${GUEST_CHECKOUT_CACHE_BASE_KEY}-${resolvedVisitorId}`
    : GUEST_CHECKOUT_CACHE_BASE_KEY;
}

function readRawCache(key: string, storage: GuestCheckoutStorage | null): string | null {
  if (clearedCacheKeys.has(key)) return null;
  if (!storage) return memoryCaches.get(key) ?? null;

  try {
    const storedValue = storage.getItem(key);
    return storedValue ?? (memoryFallbackKeys.has(key) ? (memoryCaches.get(key) ?? null) : null);
  } catch {
    return memoryCaches.get(key) ?? null;
  }
}

function readCacheByKey(
  key: string,
  storage: GuestCheckoutStorage | null,
): GuestCheckoutCacheReadResult | null {
  const raw = readRawCache(key, storage);
  if (!raw) return null;

  try {
    const value = JSON.parse(raw);
    if (!value || typeof value !== 'object') return null;

    const source = value as Record<string, unknown>;
    return {
      cache: normalizeCache(value),
      billingCleared: source[BILLING_CLEARED_FIELD] === true,
    };
  } catch {
    return null;
  }
}

function omitBilling(cache: GuestCheckoutCache | null): GuestCheckoutCache | null {
  if (!cache?.billing) return cache;

  const { billing: _billing, ...cacheWithoutBilling } = cache;
  return Object.keys(cacheWithoutBilling).length > 0 ? cacheWithoutBilling : null;
}

function clearCacheByKey(key: string, storage: GuestCheckoutStorage | null): void {
  memoryCaches.delete(key);
  memoryFallbackKeys.delete(key);
  if (!storage) return;

  clearedCacheKeys.add(key);

  try {
    storage.removeItem(key);
  } catch {
    // Clearing is best effort when browser storage is unavailable.
  }
}

function mergeCache(
  current: GuestCheckoutCache | null,
  patch: GuestCheckoutCachePatch,
): GuestCheckoutCache | null {
  const next: GuestCheckoutCache = { ...current };

  if ('contactEmail' in patch) {
    const contactEmail = normalizeText(patch.contactEmail);
    if (contactEmail) next.contactEmail = contactEmail;
    else delete next.contactEmail;
  }

  if ('shipping' in patch) {
    const shipping = normalizeAddress(patch.shipping, { createId: true });
    if (shipping) next.shipping = shipping;
    else {
      delete next.shipping;
      delete next.shippingAddressId;
    }
  }

  if ('shippingAddressId' in patch) {
    const shippingAddressId = normalizeId(patch.shippingAddressId);
    if (shippingAddressId) next.shippingAddressId = shippingAddressId;
    else delete next.shippingAddressId;
  } else if ('shipping' in patch && next.shipping?.id) {
    next.shippingAddressId = next.shipping.id;
  }

  if ('billing' in patch) {
    const billing = normalizeAddress(patch.billing);
    if (billing) next.billing = billing;
    else delete next.billing;
  }

  return normalizeCache(next);
}

export function createGuestShippingAddressId(): string {
  try {
    if (typeof globalThis.crypto?.randomUUID === 'function') {
      return `guest-${globalThis.crypto.randomUUID()}`;
    }
  } catch {
    // Fall through to the local, non-persistent fallback id.
  }

  return `guest-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * 读取当前访客隔离的结算草稿；shipping 和 billing 都只返回地址白名单字段。
 */
export function readGuestCheckoutCache(
  options: GuestCheckoutCacheOptions = {},
): GuestCheckoutCache | null {
  const storage = resolveStorage(options.storage);
  const key = getCacheKey(storage, options.visitorId);
  const keys =
    key === GUEST_CHECKOUT_CACHE_BASE_KEY
      ? [GUEST_CHECKOUT_CACHE_BASE_KEY]
      : [key, GUEST_CHECKOUT_CACHE_BASE_KEY];

  for (const cacheKey of keys) {
    const result = readCacheByKey(cacheKey, storage);
    if (result?.cache) return result.cache;

    if (result?.billingCleared && cacheKey !== GUEST_CHECKOUT_CACHE_BASE_KEY) {
      return omitBilling(readCacheByKey(GUEST_CHECKOUT_CACHE_BASE_KEY, storage)?.cache ?? null);
    }
  }

  return null;
}

/**
 * 合并结算草稿。传入 `billing: null` 仅删除独立 billing 草稿，不影响 shipping。
 */
export function saveGuestCheckoutCache(
  patch: GuestCheckoutCachePatch,
  options: GuestCheckoutCacheOptions = {},
): GuestCheckoutCache | null {
  const storage = resolveStorage(options.storage);
  const visitorId = resolveVisitorId(storage, options.visitorId);
  const key = getCacheKey(storage, visitorId);
  clearedCacheKeys.delete(key);
  const current = readGuestCheckoutCache({ storage, visitorId });
  const cache = mergeCache(current, patch);
  const billingCleared =
    key !== GUEST_CHECKOUT_CACHE_BASE_KEY &&
    ('billing' in patch
      ? patch.billing === null
      : readCacheByKey(key, storage)?.billingCleared === true);

  if (!cache && !billingCleared) {
    clearCacheByKey(key, storage);
    return null;
  }

  const serialized = JSON.stringify({
    ...cache,
    ...(billingCleared ? { [BILLING_CLEARED_FIELD]: true } : {}),
  });
  memoryCaches.set(key, serialized);

  if (!storage) {
    memoryFallbackKeys.add(key);
    return cache;
  }

  try {
    storage.setItem(key, serialized);
    memoryFallbackKeys.delete(key);
  } catch {
    memoryFallbackKeys.add(key);
    // The in-memory copy remains available for the current page lifecycle.
  }

  return cache;
}

export function clearGuestCheckoutCache(options: GuestCheckoutCacheOptions = {}): void {
  const storage = resolveStorage(options.storage);
  const visitorId = resolveVisitorId(storage, options.visitorId);
  const key = getCacheKey(storage, visitorId);
  const keys =
    key === GUEST_CHECKOUT_CACHE_BASE_KEY
      ? [GUEST_CHECKOUT_CACHE_BASE_KEY]
      : [key, GUEST_CHECKOUT_CACHE_BASE_KEY];

  for (const cacheKey of keys) {
    clearCacheByKey(cacheKey, storage);
  }
}
